import React, { useState } from 'react';
import { getAnalytics, setAnalyticsCollectionEnabled, setConsent, ConsentSettings } from "firebase/analytics";

/**
 * ConsentBanner Component
 * 
 * A GDPR/CCPA compliant consent banner that allows users to accept or decline
 * analytics and advertising data collection. The banner appears at the bottom
 * of the page and persists the user's choice in localStorage.
 * 
 * Features:
 * - Shows consent banner only if user hasn't made a choice previously
 * - Handles Firebase Analytics consent settings
 * - Configures multiple consent types (analytics, ads, personalization, etc.)
 * - Persists user preference in localStorage
 * - Responsive design with fixed positioning
 * 
 * @component
 * @returns {JSX.Element|null} The consent banner UI or null if already consented
 */
const ConsentBanner = () => {
    // State to determine whether the consent banner should be displayed
    const [showBanner, setShowBanner] = useState(!localStorage.getItem('analyticsConsent'));

    /**
     * Handles user acceptance of analytics and advertising consent.
     * 
     * Actions performed:
     * 1. Saves consent decision to localStorage
     * 2. Hides the consent banner
     * 3. Enables Firebase Analytics collection
     * 4. Grants all consent types (analytics, ads, personalization, functionality, security)
     * 
     * @function
     */
    const handleAccept = () => {
        // Save user's consent decision in localStorage
        localStorage.setItem('analyticsConsent', 'true');
        // Hide the consent banner
        setShowBanner(false);

        // Enable analytics and pass consent signals
        const analytics = getAnalytics();
        setAnalyticsCollectionEnabled(analytics, true);

        // Pass consent signals to Firebase
        setConsent({
            analytics_storage: 'granted', // Consent for analytics data collection
            ad_storage: 'granted', // Consent for ad-related data storage
            personalization_storage: 'granted', // Consent for personalized ads
            functionality_storage: 'granted', // Functionality storage consen
            security_storage: 'granted', // Ads Measurement consent
        });
    };

    /**
     * Handles user decline of analytics and advertising consent.
     * 
     * Actions performed:
     * 1. Saves decline decision to localStorage
     * 2. Hides the consent banner
     * 3. Disables Firebase Analytics collection
     * 4. Denies all consent types (analytics, ads, personalization, functionality, security)
     * 
     * This ensures compliance with privacy regulations by respecting user's choice
     * to opt-out of data collection.
     * 
     * @function
     */
    const handleDecline = () => {
        // Save user's consent decision in localStorage
        localStorage.setItem('analyticsConsent', 'false');
        // Hide the consent banner
        setShowBanner(false);

        // Disable analytics and pass consent signals
        const analytics = getAnalytics();
        setAnalyticsCollectionEnabled(analytics, false);

        setConsent({
            analytics_storage: 'denied', // Consent for analytics data collection
            ad_storage: 'denied', // Consent for ad-related data storage
            personalization_storage: 'denied', // Ads Personalization consent
            functionality_storage: 'denied', // Functionality storage consen
            security_storage: 'denied', // Ads Measurement consent
        });
    };

    // Early return if user has already made a consent choice
    // This prevents the banner from showing on subsequent visits
    if (!showBanner) return null;

    return (
        <div style={{ position: 'fixed', bottom: 0, width: '100%', background: '#333', color: '#fff', padding: '10px', textAlign: 'center' }}>
            <p>
                We use cookies and similar technologies to enhance your experience.<br />
                By clicking "Accept", you agree to the collection of behavioral analytics and personalized ads.
            </p>
            {/* Accept button - grants all consent types */}
            <button onClick={handleAccept} style={{ marginRight: '10px', padding: '5px 10px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '5px' }}>
                Accept
            </button>
            {/* Decline button - denies all consent types */}
            <button onClick={handleDecline} style={{ padding: '5px 10px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '5px' }}>
                Decline
            </button>
        </div>
    );
};

export default ConsentBanner;